import { observer } from "mobx-react-lite";
import { useCallback, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import Scope from "./Scope";
import SignalFormTitle from "./SignalFormTitle";
import SignalTestDrawer from "./SignalTestDrawer";
import { useSignalFormStore } from "./signal-form-store";
import {
  getSignalFormSaveDisabledReason,
  haveBindingsChanged,
  hooksToBindingPayloads,
  isSignalFormValid,
  validateHooks,
} from "./utils";

import { Switch } from "@/components/switch";
import BreadcrumbsPageWrapper from "@/components/UnifiedRegistry/BreadcrumbsPageWrapper";
import SaveButton from "@/components/ui/SaveButton";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/context/auth-context";
import { useUser } from "@/context/user-context";
import { EditSignalRequest, Signal, useCreateSignal, useEditSignal } from "@/queries/signals";
import { matchesShortcut } from "@/utils/shortcuts";
import { displayErrorToast } from "@/utils/toast";

interface SignalFormPageContentProps {
  mode: "create" | "edit";
  signal?: Signal;
}

const SignalFormPageContent = observer(({ mode, signal }: SignalFormPageContentProps) => {
  const location = useLocation();
  const navigate = useNavigate();
  const userToken = useAuth()?.userToken ?? "";
  const workspaceId = useUser()?.activeWorkspaceId ?? 0;
  const createSignalMutation = useCreateSignal();
  const editSignalMutation = useEditSignal();
  const store = useSignalFormStore();

  const isSaving = createSignalMutation.isPending || editSignalMutation.isPending;
  const canSave = isSignalFormValid(store.formState);
  const saveDisabledReason = getSignalFormSaveDisabledReason(store.formState);
  const breadcrumbLabel = mode === "create" ? "New signal" : store.name;
  const returnTo = (location.state as { returnTo?: string } | null)?.returnTo;

  const handleCancel = () => {
    navigate(returnTo ?? `/workspace/${workspaceId}/signals`);
  };

  const handleTitleEdit = useCallback(
    ({
      newTitle,
      onSuccess,
      onError,
    }: {
      newTitle: string;
      onSuccess?: (newTitle?: string) => void;
      onError?: () => void;
    }) => {
      if (!signal) {
        onError?.();
        return;
      }

      editSignalMutation.mutate(
        {
          workspaceId,
          signalId: signal.id,
          userToken,
          name: newTitle,
          successMessage: "Signal renamed",
        },
        {
          onSuccess: () => {
            store.setName(newTitle);
            onSuccess?.(newTitle);
          },
          onError: () => {
            onError?.();
          },
        },
      );
    },
    [editSignalMutation, signal, store, userToken, workspaceId],
  );

  const handleSave = useCallback(async () => {
    const trimmedName = store.name.trim();
    const trimmedPrompt = store.prompt.trim();

    if (!trimmedName) {
      displayErrorToast("Name is required.");
      return;
    }

    if (!trimmedPrompt) {
      displayErrorToast("Signal statement is required.");
      return;
    }

    const hooksError = validateHooks(store.formState.hooks);
    if (hooksError) {
      displayErrorToast(hooksError);
      return;
    }

    const bindings = hooksToBindingPayloads(store.formState.hooks);

    if (mode === "create") {
      const response = await createSignalMutation.mutateAsync({
        workspaceId,
        userToken,
        name: trimmedName,
        prompt: trimmedPrompt,
        enabled: store.enabled,
        bindings,
      });
      navigate(`/workspace/${workspaceId}/signals/${response.signal.id}/edit`, {
        state: returnTo ? { returnTo } : undefined,
      });
      return;
    }

    if (!signal) {
      return;
    }

    const editPayload: EditSignalRequest = {};
    if (trimmedName !== signal.name) {
      editPayload.name = trimmedName;
    }
    if (trimmedPrompt !== signal.prompt) {
      editPayload.prompt = trimmedPrompt;
    }
    if (store.enabled !== signal.enabled) {
      editPayload.enabled = store.enabled;
    }

    const deletedBindingIds = signal.bindings
      .filter(existing => !store.formState.hooks.some(hook => hook.bindingId === existing.id))
      .map(existing => existing.id);

    if (deletedBindingIds.length > 0 || haveBindingsChanged(signal.bindings, bindings)) {
      editPayload.bindings = bindings;
      if (deletedBindingIds.length > 0) {
        editPayload.deleted_binding_ids = deletedBindingIds;
      }
    }

    await editSignalMutation.mutateAsync({
      workspaceId,
      signalId: signal.id,
      userToken,
      ...editPayload,
    });
  }, [
    createSignalMutation,
    editSignalMutation,
    mode,
    navigate,
    returnTo,
    signal,
    store,
    userToken,
    workspaceId,
  ]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (matchesShortcut(event, { mod: true, key: "s" })) {
        event.preventDefault();
        if (!isSaving && canSave) {
          void handleSave();
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [canSave, handleSave, isSaving]);

  return (
    <BreadcrumbsPageWrapper
      className="flex h-full min-h-0 flex-1 flex-col pb-1"
      showBackButton
      onBackClick={handleCancel}
      updateDocumentTitle={false}
      items={[
        {
          id: null,
          label: "Signals",
          href: `/workspace/${workspaceId}/signals`,
        },
        {
          id: signal?.id ?? null,
          label: breadcrumbLabel,
        },
      ]}
    >
      <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
        <div className="mb-6 flex shrink-0 items-start justify-between gap-6">
          <div className="min-w-0 flex-1">
            <SignalFormTitle
              mode={mode}
              title={store.name}
              onTitleEdit={mode === "edit" ? handleTitleEdit : undefined}
              onCreateNameChange={name => store.setName(name)}
            />
          </div>
          <div className="flex shrink-0 items-center gap-4">
            {mode === "edit" && (
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-700">
                  {store.enabled ? "Enabled" : "Disabled"}
                </span>
                <Switch
                  checked={store.enabled}
                  onChange={value => store.setEnabled(value)}
                  size="sm"
                />
              </div>
            )}
            <SaveButton
              label="Save signal"
              isSaving={isSaving}
              disabled={isSaving || !canSave}
              tooltipContent={canSave ? undefined : (saveDisabledReason ?? undefined)}
              onClick={() => {
                void handleSave();
              }}
            />
          </div>
        </div>

        <div className="min-h-0 flex-1 space-y-10 overflow-y-auto pb-4">
          <section>
            <Textarea
              id="signal-prompt"
              value={store.prompt}
              onChange={event => store.setPrompt(event.target.value)}
              placeholder="Is the user's message written in English?"
              rows={4}
              className="min-h-[120px] resize-y"
            />
          </section>
          <Scope
            hooks={store.formState.hooks}
            onHookChange={(hookId, hook) => store.updateHook(hookId, () => hook)}
            onAddHook={() => store.addHook()}
            onRemoveHook={hookId => store.removeHook(hookId)}
          />
        </div>

        <SignalTestDrawer workspaceId={workspaceId} prompt={store.prompt} />
      </div>
    </BreadcrumbsPageWrapper>
  );
});

export default SignalFormPageContent;
